import { Entypo, Ionicons, MaterialIcons } from "@expo/vector-icons";
// NOTE: Adding @sentry/react to the dependencies causes an error because of dependencies it installs.
// Our other sentry dependencies (@sentry/react-native) already install @sentry/react so we can ignore this error.
// eslint-disable-next-line import/no-extraneous-dependencies
import * as Sentry from "@sentry/react";
import _ from "lodash";
import { Button, Icon, IIconButtonProps, Image, Link, Text, useTheme } from "native-base";
import platform from "platform-detect";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScreenWidth } from "react-native-elements/dist/helpers";
import { useSelector } from "react-redux";

import { Scale } from "../constants";
import { IS_NOT_DESKTOP } from "../constants/helperFunction";
import { useExternalLink } from "../hooks/useExternalLink";
import { MAGIC_LINK_HELP_ARTICLE } from "../screens/MagicLinkSentScreen";
import backendApi from "../services/backendApi";
import type { User } from "../services/backendTypes";
import { getBrandSelector, getIsLoadingBrandSelector } from "../slices/brandSlice";
import analyticsInterface, { analyticsJson } from "./analyticsHelpers";
import { isMobilePlatform, openURL } from "./generalHelpers";
import { getHostname, getManagedCustomerPortalSubdomain } from "./userHelpers";

const { isIntercomLoaded, trackEvent } = analyticsInterface;

const SUPPORT_WEBSITE = "https://help.nutrition-app.com/";

const KNOWLEDGE_BASE_URL = "https://kennisbank.weekmeals.co/docs/";

export const LOGIN_CODE_HELP_ARTICLE_URL = "https://help.nutrition-app.com/articles/11460873-having-trouble-logging-in";

type Props = {
  width?: string;
  ml?: string;
};

const { useUsersStripeCustomerPortalCreateMutation, useUsersBrandRetrieveQuery } = backendApi;

export const KnowledgeBaseLink = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Button
      onPress={() => openURL(KNOWLEDGE_BASE_URL)}
      leftIcon={<Icon as={Ionicons} name="book-outline" color="function.50" />}
      mt="3"
      nativeID="knowledgeBaseButton"
      variant="outline"
      borderColor="function.40"
      borderWidth={2}
      borderRadius={theme.borderRadii.button.default}
      _text={{ color: "function.50" }}
      _pressed={{ bg: "function.10", borderColor: "function.50", _text: { color: "function.50" } }}
      {...props}
    >
      {t("general.knowledge_base")}
    </Button>
  );
};

const NO_CODE_STRIPE_CUSTOMER_PORTAL_URL = "https://billing.stripe.com/p/login/eVa6sjehu85DeDmdQQ";

type StripeCustomerPortalLinkProps = {
  isDesktop: boolean;
  isUpgradeNowButton?: boolean;
  variant?: string;
  width?: string | number;
  size?: string;
  iconType?: "ionicons" | "material";
  bg?: string;
  _pressed?: object;
  px?: number;
  shadow?: number;
};
export const StripeCustomerPortalLink = ({
  isDesktop,
  isUpgradeNowButton,
  variant = "solid",
  width,
  size,
  iconType = "ionicons",
  bg,
  _pressed,
  px,
  shadow,
}: StripeCustomerPortalLinkProps): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [createStripeCustomerPortal, { isLoading: isLoadingPortal }] = useUsersStripeCustomerPortalCreateMutation();
  const { isLoading: isLoadingLink, error, openLink } = useExternalLink();
  const [portalUrl, setPortalUrl] = useState<string | null>(null);
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);
  const isLoading = Boolean(isLoadingPortal || isLoadingLink || isFetchingUrl);

  // Fetch portal URL when component mounts
  useEffect(() => {
    const fetchPortalUrl = async (): Promise<void> => {
      if (portalUrl !== null) return; // Already fetched

      try {
        setIsFetchingUrl(true);
        const stripeCustomerPortal = await createStripeCustomerPortal().unwrap();
        setPortalUrl(stripeCustomerPortal.portal_url);
      } catch (e) {
        console.error("Failed to prefetch portal URL:", e);
        Sentry.captureException(e);
        // Set fallback URL on error
        setPortalUrl(NO_CODE_STRIPE_CUSTOMER_PORTAL_URL);
      } finally {
        setIsFetchingUrl(false);
      }
    };

    fetchPortalUrl().catch((e) => {
      console.error("Error in fetchPortalUrl:", e);
      Sentry.captureException(e);
    });
  }, [createStripeCustomerPortal, portalUrl]);

  const onPress = async (): Promise<void> => {
    if (portalUrl) {
      // We already have the URL, open it directly
      await openLink(portalUrl).catch((err) => {
        console.error("Failed to open URL:", err);
        Sentry.captureException(err);
      });
    } else {
      // Fallback if URL isn't ready yet
      try {
        const stripeCustomerPortal = await createStripeCustomerPortal().unwrap();
        await openLink(stripeCustomerPortal.portal_url).catch((err) => {
          console.error("Failed to open URL:", err);
          Sentry.captureException(err);
        });
      } catch (e) {
        Sentry.captureException(e);
        // Fall back to NO_CODE_STRIPE_CUSTOMER_PORTAL_URL if the API call fails
        await openLink(NO_CODE_STRIPE_CUSTOMER_PORTAL_URL).catch((err) => {
          console.error("Failed to open fallback URL:", err);
          Sentry.captureException(err);
          alert(t("general.generic_error_message"));
        });
      }
    }
  };

  const optionalProps: IIconButtonProps = isUpgradeNowButton
    ? { width: isDesktop ? "10%" : "40%", ml: isDesktop ? "80%" : "30%", backgroundColor: "warning.400" }
    : { width: width || "auto" };

  let icon;
  if (iconType === "material") {
    icon = (
      <Icon
        as={MaterialIcons}
        name="payment"
        size={size || "md"}
        color={variant === "outline" ? "function.50" : "white"}
      />
    );
  } else {
    icon = (
      <Icon
        as={Ionicons}
        name="card-outline"
        size={size || "md"}
        color={variant === "outline" ? "function.50" : "white"}
      />
    );
  }

  return (
    <Button
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onPress={onPress}
      leftIcon={icon}
      nativeID="stripeCustomerPortalButton"
      variant={variant}
      size={size}
      isLoading={isLoading}
      isLoadingText={t("general.loading")}
      borderColor={variant === "outline" ? "function.40" : undefined}
      bg={bg || (variant === "outline" ? "transparent" : "function.50")}
      borderWidth={variant === "outline" ? 2 : undefined}
      borderRadius={theme.borderRadii.button.default}
      _text={
        variant === "outline"
          ? { color: "function.50" }
          : { color: "white", fontSize: "sm", fontFamily: theme.fonts.body }
      }
      _pressed={
        _pressed ||
        (variant === "outline"
          ? { bg: "function.10", borderColor: "function.50", _text: { color: "function.50" } }
          : { bg: "function.40", _text: { color: "function.10" } })
      }
      px={px}
      shadow={shadow}
      {...optionalProps}
    >
      {t(isUpgradeNowButton ? "general.upgrade_now_button_text" : "general.manage_billing")}
    </Button>
  );
};

const HUBSPOT_DEMO_CALENDAR_URL = "https://meetings.hubspot.com/martin-foley/weekmeals-office-hours";

type BookACallLinkProps = {
  isDesktop: boolean;
  email?: string;
};
export const BookACallLink = ({ isDesktop, email }: BookACallLinkProps): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();

  const optionalProps: IIconButtonProps = {
    width: isDesktop ? "10%" : "40%",
    ml: isDesktop ? "80%" : "30%",
    mt: "3",
  };

  const onPressBookACallButton = (): void => {
    trackEvent(analyticsJson.events.click_book_a_demo_button.name);
    openURL(email ? `${HUBSPOT_DEMO_CALENDAR_URL}?email=${encodeURIComponent(email)}` : HUBSPOT_DEMO_CALENDAR_URL);
  };

  return (
    <Button
      onPress={onPressBookACallButton}
      leftIcon={<Icon as={Ionicons} name="call-outline" color="function.50" />}
      nativeID="bookACallButton"
      variant="outline"
      borderColor="function.40"
      borderWidth={2}
      borderRadius={theme.borderRadii.button.default}
      _text={{ color: "function.50" }}
      _pressed={{ bg: "function.10", borderColor: "function.50", _text: { color: "function.50" } }}
      {...optionalProps}
    >
      {t("general.get_a_demo")}
    </Button>
  );
};

const REFERRAL_PROGRAMME_URL = "https://referrals.weekmeals.co";

export const ReferralProgrammeLink = ({ isDesktop }: { isDesktop: boolean }): JSX.Element => {
  const { t } = useTranslation();

  const optionalProps: IIconButtonProps = {
    width: isDesktop ? null : "60%",
    ml: isDesktop ? "70%" : "20%",
    mr: isDesktop ? "10%" : null,
    mt: "3",
  };

  const onPressReferralProgrammeButton = (): void => {
    trackEvent(analyticsJson.events.click_referral_programme_button.name);

    openURL(REFERRAL_PROGRAMME_URL);
  };

  return (
    <Button
      onPress={onPressReferralProgrammeButton}
      leftIcon={<Icon as={Ionicons} name="gift-outline" color="white" />}
      nativeID="referralProgrammeButton"
      backgroundColor={"warning.400"}
      {...optionalProps}
    >
      {t("coach_general.refer_a_friend_button")}
    </Button>
  );
};

type ContactSupportLinkProps = {
  user: User | null;
  width?: string | number;
  size?: string;
  mt?: string | number;
  shadow?: number;
  borderWidth?: number;
};

export const ContactSupportLink = ({
  user,
  width = "60%",
  size = "lg",
  mt = 2,
  shadow = 3,
  borderWidth = 2,
}: ContactSupportLinkProps): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const customSupportSubdomain = getManagedCustomerPortalSubdomain(getHostname());

  // NOTE: If we are not using a custom support subdomain then there is no need to retrieve the url
  const { data: brand } = useUsersBrandRetrieveQuery(
    { subdomain: customSupportSubdomain },
    { skip: !customSupportSubdomain }
  );

  const onPressContactSupportButton = (): void => {
    const organisationHasCustomSupportUrl = brand && brand?.support_url;

    if (!organisationHasCustomSupportUrl) {
      if (isIntercomLoaded()) {
        analyticsInterface.openIntercomMessenger();
        return;
      }
      // If Intercom is not loaded then fallback to the website
      openURL(SUPPORT_WEBSITE);
      return;
    }

    openURL(brand?.support_url);
  };

  return (
    <Button
      onPress={onPressContactSupportButton}
      mt={mt}
      nativeID="contactSupportButton"
      variant="outline"
      borderColor="function.20"
      borderWidth={borderWidth}
      borderRadius={theme.borderRadii.button.default}
      shadow={shadow}
      width={width}
      size={size}
      _text={{ color: "function.50" }}
      _pressed={{ bg: "function.10", borderColor: "function.30", _text: { color: "function.50" } }}
    >
      {t("general.get_help")}
    </Button>
  );
};

export const TroubleLoggingInLink = (): JSX.Element => {
  const { t } = useTranslation();
  const customSupportSubdomain = getManagedCustomerPortalSubdomain(getHostname());

  const {
    data: organisation,
    isError,
    error,
  } = useUsersBrandRetrieveQuery({ subdomain: customSupportSubdomain }, { skip: !customSupportSubdomain });
  const getSupportUrl = (): string => {
    if (organisation && organisation?.support_url) {
      return organisation.support_url;
    }

    return MAGIC_LINK_HELP_ARTICLE;
  };

  return (
    <Link isExternal onPress={() => openURL(getSupportUrl())}>
      {t("login_screen.trouble_logging_in_help")}
    </Link>
  );
};

const TROUBLE_INSTALLING_PWA_ARTICLE_URL =
  "https://help.nutrition-app.com/articles/10581245-how-to-install-nutrition-app-on-your-mobile-device";

const TROUBLE_INSTALLING_PWA_ANDROID_ARTICLE_URL =
  "https://help.nutrition-app.com/articles/11195792-how-to-install-the-app-on-your-android-mobile-device";
const TROUBLE_INSTALLING_PWA_IOS_ARTICLE_URL =
  "https://help.nutrition-app.com/articles/11195232-how-to-install-the-app-on-your-ios-mobile-device";

export const TroubleInstallingPWAArticleComponent = (): JSX.Element => {
  const { t } = useTranslation();
  let articleUrl = TROUBLE_INSTALLING_PWA_ARTICLE_URL;
  if (platform.ios) {
    articleUrl = TROUBLE_INSTALLING_PWA_IOS_ARTICLE_URL;
  } else if (platform.android) {
    articleUrl = TROUBLE_INSTALLING_PWA_ANDROID_ARTICLE_URL;
  }

  // console.log("articleUrl in TroubleInstallingPWAArticleComponent", articleUrl);

  return HelpArticleButton({
    articleUrl,
    ctaText: t("pwa_install_screen.need_help_installing_text"),
  });
};

export const HelpArticleButton = ({ articleUrl, ctaText }: { articleUrl: string; ctaText?: string }): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Button
      onPress={() => {
        // console.log("in HelpArticleButton.onPress() with: ", articleUrl);

        openURL(articleUrl);
      }}
      leftIcon={<Icon as={Ionicons} name="help-circle-outline" size="lg" color="white" />}
      mt="4"
      size="lg"
      bg="warning.400"
      _hover={{ bg: "warning.500" }}
      _pressed={{ bg: "warning.600" }}
      shadow={3}
      borderRadius={theme.borderRadii.button.default}
      px="12"
      py="4"
      borderWidth="0"
      _text={{
        color: "white",
        fontWeight: "bold",
        fontSize: "md",
        letterSpacing: "0.3",
      }}
    >
      {ctaText || t("general.get_help")}
    </Button>
  );
};

export const AppImage = (): JSX.Element => {
  const brand = useSelector(getBrandSelector);
  const isLoadingBrand = useSelector(getIsLoadingBrandSelector);

  const imageSize = Scale(ScreenWidth * 0.8 * (IS_NOT_DESKTOP ? 0.35 : 0.2));

  if (isLoadingBrand) {
    // We return a placeholder div to prevent layout shift
    return (
      <div
        style={{
          width: imageSize,
          height: imageSize,
        }}
      ></div>
    );
  }

  return (
    <Image
      source={{ uri: brand?.logo }}
      style={{
        width: imageSize,
        height: imageSize,
      }}
      resizeMode="contain"
      marginY={8}
    />
  );
};
